import React, { useState, useCallback } from 'react';
import type { ProcessNoticeResponse } from '../types/notice';
import {
  formatDate,
  formatCurrency,
  getDateUrgency,
  getConfidenceLevel,
  getConfidenceColorClass,
  getUrgencyColorClass,
} from '../utils/validation';
import { RichText } from '../utils/linkify';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import { HUBSPOT_MEETING_URL } from '@/config/constants';

interface NoticeResultsProps {
  result: ProcessNoticeResponse;
  isCreatingTicket?: boolean;
  onCreateTicket?: () => void;
  onStartOver?: () => void;
  className?: string;
}

export const NoticeResults: React.FC<NoticeResultsProps> = ({
  result,
  isCreatingTicket = false,
  onCreateTicket,
  onStartOver,
  className = '',
}) => {
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    dates: true,
    monetary: true,
    details: false,
  });

  const [checkedActions, setCheckedActions] = useState<Set<number>>(new Set());
  const [expandedActions, setExpandedActions] = useState<Set<number>>(new Set());

  const toggleActionCheck = useCallback((index: number) => {
    setCheckedActions(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const toggleActionExpand = useCallback((index: number) => {
    setExpandedActions(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const confidenceLevel = getConfidenceLevel(result.confidence_score);
  const confidencePercentage = Math.round(result.confidence_score * 100);
  const displayClassification = result.notice_classification === "Unknown Notice Type" ? "Needs Verification" : result.notice_classification;

  const hasKeyDates = Object.values(result.key_dates).some(date => date !== null);
  const hasMonetaryInfo = Object.values(result.monetary_info).some(amount => amount !== null);
  const hasActionItems = result.action_items.length > 0;

  const SectionHeader: React.FC<{
    title: string;
    section: keyof typeof expandedSections;
    badge?: string;
  }> = ({ title, section, badge }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 hover:bg-accent/50 transition-colors rounded-lg"
    >
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {badge && (
          <span className="px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground rounded-full">
            {badge}
          </span>
        )}
      </div>
      <svg
        className={`w-4 h-4 text-muted-foreground transition-transform ${
          expandedSections[section] ? 'rotate-180' : ''
        }`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-bold text-foreground">
          Analysis Complete
        </h2>
        <div className="flex items-center justify-center gap-3">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
            {displayClassification}
          </span>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getConfidenceColorClass(confidenceLevel)}`}>
            {confidencePercentage}% Confidence
          </span>
        </div>
      </div>

      {/* Action Checklist */}
      {hasActionItems && (
        <div className="rounded-xl border bg-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">What You Need to Do</h3>
            <span className="text-xs text-muted-foreground">
              {checkedActions.size}/{result.action_items.length} done
            </span>
          </div>
          <ul className="space-y-2">
            {result.action_items
              .sort((a, b) => {
                const priorityOrder = { "High": 1, "Medium": 2, "Low": 3 };
                return priorityOrder[a.priority] - priorityOrder[b.priority];
              })
              .map((item, index) => {
                const isChecked = checkedActions.has(index);
                const isExpanded = expandedActions.has(index);
                const hasDetails = !!item.details;
                const hasDeadline = !!item.deadline;
                const deadlineUrgency = item.deadline ? getDateUrgency(item.deadline) : 'normal';
                const priorityBadgeColors = {
                  High: 'bg-red-100 text-red-800',
                  Medium: 'bg-yellow-100 text-yellow-800',
                  Low: 'bg-emerald-100 text-emerald-800',
                };

                return (
                  <li
                    key={index}
                    className={`rounded-lg border transition-colors ${
                      isChecked ? 'bg-muted/50 border-border' : 'bg-card border-border'
                    }`}
                  >
                    <div className="flex items-start gap-3 p-3">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleActionCheck(index)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => hasDetails && toggleActionExpand(index)}
                            className={`text-left text-sm ${isChecked ? 'line-through text-muted-foreground' : 'text-foreground'} ${hasDetails ? 'cursor-pointer hover:text-primary' : 'cursor-default'}`}
                          >
                            {item.action}
                            {hasDetails && (
                              <svg
                                className={`inline-block ml-1 w-3.5 h-3.5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            )}
                          </button>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${priorityBadgeColors[item.priority]}`}>
                            {item.priority}
                          </span>
                        </div>
                        {hasDeadline && (
                          <div className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${getUrgencyColorClass(deadlineUrgency)}`}>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Due: {formatDate(item.deadline!)}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Expandable details */}
                    {hasDetails && (
                      <div
                        className="grid"
                        style={{
                          gridTemplateRows: isExpanded ? '1fr' : '0fr',
                          transition: 'grid-template-rows 300ms ease-in-out',
                        }}
                      >
                        <div style={{ overflow: 'hidden', minHeight: 0 }}>
                          <div className="px-3 pb-3 pl-10 border-t border-border/50 pt-3">
                            <RichText text={item.details!} />
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
          </ul>
        </div>
      )}

      {/* Executive Summary */}
      <div className="rounded-xl border bg-card">
        <SectionHeader title="Executive Summary" section="summary" />
        <div className="grid" style={{ gridTemplateRows: expandedSections.summary ? '1fr' : '0fr', transition: 'grid-template-rows 300ms ease-in-out' }}>
          <div style={{ overflow: 'hidden', minHeight: 0 }}>
            <div className="px-4 pb-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {result.executive_summary}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Dates */}
      {hasKeyDates && (
        <div className="rounded-xl border bg-card">
          <SectionHeader
            title="Important Dates"
            section="dates"
            badge={Object.values(result.key_dates).filter(d => d).length.toString()}
          />
          <div className="grid" style={{ gridTemplateRows: expandedSections.dates ? '1fr' : '0fr', transition: 'grid-template-rows 300ms ease-in-out' }}>
            <div style={{ overflow: 'hidden', minHeight: 0 }}>
              <div className="px-4 pb-4 space-y-2">
                {Object.entries(result.key_dates).map(([key, date]) => {
                  if (!date) return null;
                  const urgency = getDateUrgency(date);
                  const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  return (
                    <div
                      key={key}
                      className={`flex items-center justify-between p-3 rounded-lg border ${getUrgencyColorClass(urgency)}`}
                    >
                      <div>
                        <p className="text-sm font-medium">{label}</p>
                        <p className="text-xs mt-0.5">{formatDate(date)}</p>
                      </div>
                      {urgency === 'urgent' && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">Urgent</span>
                      )}
                      {urgency === 'warning' && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">Soon</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Monetary Information */}
      {hasMonetaryInfo && (
        <div className="rounded-xl border bg-card">
          <SectionHeader title="Financial Information" section="monetary" />
          <div className="grid" style={{ gridTemplateRows: expandedSections.monetary ? '1fr' : '0fr', transition: 'grid-template-rows 300ms ease-in-out' }}>
            <div style={{ overflow: 'hidden', minHeight: 0 }}>
              <div className="px-4 pb-4 space-y-2">
                {Object.entries(result.monetary_info).map(([key, amount]) => {
                  if (amount === null) return null;
                  const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  const isTotal = key === 'total_amount';
                  return (
                    <div
                      key={key}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        isTotal ? 'bg-muted border border-border' : 'bg-muted/50'
                      }`}
                    >
                      <span className={`text-sm ${isTotal ? 'font-semibold' : 'font-medium'} text-foreground`}>
                        {label}
                      </span>
                      <span className={`text-sm ${isTotal ? 'font-bold' : 'font-medium'} text-foreground`}>
                        {formatCurrency(amount)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Information */}
      {result.contact_info && Object.values(result.contact_info).some(v => v) && (
        <div className="rounded-xl border bg-card p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Agency Contact Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {result.contact_info.phone && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
                <svg className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <a href={`tel:${result.contact_info.phone.replace(/\D/g, '')}`} className="text-sm text-primary underline underline-offset-2 hover:text-primary/80">
                    {result.contact_info.phone}
                  </a>
                </div>
              </div>
            )}
            {result.contact_info.website && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
                <svg className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <div>
                  <p className="text-xs text-muted-foreground">Website</p>
                  <a href={result.contact_info.website.match(/^https?:\/\//) ? result.contact_info.website : `https://${result.contact_info.website}`} target="_blank" rel="noopener noreferrer" className="text-sm text-primary underline underline-offset-2 hover:text-primary/80 break-all">
                    {result.contact_info.website}
                  </a>
                </div>
              </div>
            )}
            {result.contact_info.online_portal && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
                <svg className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                <div>
                  <p className="text-xs text-muted-foreground">Online Portal</p>
                  <a href={result.contact_info.online_portal.match(/^https?:\/\//) ? result.contact_info.online_portal : `https://${result.contact_info.online_portal}`} target="_blank" rel="noopener noreferrer" className="text-sm text-primary underline underline-offset-2 hover:text-primary/80 break-all">
                    {result.contact_info.online_portal}
                  </a>
                </div>
              </div>
            )}
            {result.contact_info.mailing_address && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
                <svg className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div>
                  <p className="text-xs text-muted-foreground">Mailing Address</p>
                  <p className="text-sm text-foreground">{result.contact_info.mailing_address}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CTAs */}
      <div className="rounded-xl border bg-card p-6 space-y-4">
        <h3 className="text-sm font-semibold text-foreground text-center">What would you like to do next?</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Have DianaHR Handle This */}
          <Button
            onClick={onCreateTicket}
            disabled={isCreatingTicket}
            className="h-auto py-4 flex-col gap-1"
          >
            {isCreatingTicket ? (
              <>
                <Spinner className="size-4" />
                <span className="text-sm font-semibold">Sending...</span>
              </>
            ) : (
              <>
                <span className="text-sm font-semibold">Have DianaHR Handle This</span>
                <span className="text-xs opacity-80 font-normal">We'll take care of compliance for you</span>
              </>
            )}
          </Button>

          {/* Book a Call */}
          <Button
            variant="outline"
            className="h-auto py-4 flex-col gap-1"
            asChild
          >
            <a
              href={HUBSPOT_MEETING_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="text-sm font-semibold">Book a Call</span>
              <span className="text-xs text-muted-foreground font-normal">Talk to an HR expert</span>
            </a>
          </Button>
        </div>

        <div className="flex justify-center gap-3 pt-2">
          <Button variant="ghost" size="sm" onClick={onStartOver}>
            Process Another Notice
          </Button>
          {/* Print Summary - hidden for now */}
          {/* <Button variant="ghost" size="sm" onClick={() => window.print()}>
            Print Summary
          </Button> */}
        </div>
      </div>
    </div>
  );
};
